import React, { useEffect, useMemo, Fragment } from 'react';
import Head from 'next/head';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Grid } from 'antd';
import { useRouter } from 'next/router';
import pathToRegexp from 'path-to-regexp';
import SiderMenu from 'src/components/SiderMenu';
import GlobalHeader from 'src/components/GlobalHeader';
import ErrorBoundary from 'src/components/ErrorBoundary';
import { appReadySelector, isInAppSelector, localeSelector, isGroupUserSelector } from 'src/selector/app';
import { currentUserSelector, menusSelector } from 'src/selector/user';
import { collapsedSelector } from 'src/selector/app';
import { restauantProfileSelector } from 'src/selector/setting';
import { fetchRestaurantProfile } from 'src/actions/setting';
import { actions as appActions, fetchRestaurantList } from 'src/actions/app';
import { fetchCurrentEmployeeInfo, loadMenus, loadPermissionCodes } from 'src/actions/user';
import Fonts from 'src/third_party/Fonts';
import { DISTRIBUTION_MODES } from 'src/consts';
import { getItemName } from 'src/utils/utils';
import styles from './basic.less';
import {
  LOYALTY,
  LOYALTY_AUTOPOLIT,
  LOYALTY_MANUAL_CAMPAIGN,
  SETTING_WAITLIST,
  SETTING_RESERVATION,
  SETTING,
  REPORTS,
  REPORTS_PAYOUT,
  PROMOTIONS,
  MENU,
  MENU_SETUP,
  SETTING_QRCODE_ORDERING,
  SETTING_ONLINE_ORDERING,
} from 'src/consts/permissionCodes';
import { extractRestaurantId } from 'src/utils/user';
import { subscribeRestaurantChannel } from 'src/third_party/pusher';
import { IDENTITY_RESTAURATEUR } from 'src/consts/user';
import { PageMenuSetup, PageGlobalPreferenceSettings } from 'src/consts/routes';
import { mixpanel } from '../third_party/Mixpanel';
const { Content } = Layout;
const { useBreakpoint } = Grid;

const SmallLogo = `${process.env.ASSETS_PREFIX}/assets/logo_sm.png`;
const logo = `${process.env.ASSETS_PREFIX}/assets/logo.png`;

const getPageTitle = (pathname = '/dashboard', locale, menus) => {
  const menu = menus.find(
    (menuItem) => (!!menuItem.path && pathToRegexp(menuItem.path).test(pathname)) || pathname.includes(menuItem.path)
  );

  if (menu) {
    return `POS-` + getItemName(menu, locale);
  }

  return 'POS';
};

const BasicLayout = (props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const locale = useSelector(localeSelector);
  const { menus, loadingMenu } = useSelector(menusSelector);
  const { restaurantInfo, settings } = useSelector(restauantProfileSelector);
  const { currentUser, currentRestaurantId } = useSelector(currentUserSelector);
  const isGroup = useSelector(isGroupUserSelector);
  const isInApp = useSelector(isInAppSelector);
  const appReady = useSelector(appReadySelector);
  const collapsed = useSelector(collapsedSelector);
  const { fixedSideBar = true, sideBarWidth = 240, layoutBackgroundColor = '#f5f5f5' } = props;

  useEffect(() => {
    if (!appReady || !currentRestaurantId || isGroup) return;

    dispatch(fetchRestaurantList());
    dispatch(loadMenus(isInApp));
    dispatch(loadPermissionCodes());
    dispatch(fetchRestaurantProfile(true));

    if (!isInApp) {
      // subscribe restaurant channel, if is subscribed will do nothing
      subscribeRestaurantChannel(currentRestaurantId);
    }
  }, [appReady, isInApp, currentRestaurantId, isGroup]);

  useEffect(() => {
    if (!appReady || !currentUser || !currentUser.sub) return;
    const { restaurant_contracts = [] } = restaurantInfo || {};
    if (!restaurant_contracts.find((_) => _.distribution_mode === DISTRIBUTION_MODES.POS)) return;
    const { identity } = extractRestaurantId(currentUser);
    if (identity === IDENTITY_RESTAURATEUR) {
      dispatch(fetchCurrentEmployeeInfo(currentUser.sub));
    }
  }, [currentUser, appReady, restaurantInfo.restaurant_contracts]);

  useEffect(() => {
    if (settings) {
      mixpanel.toggleTracking(settings.enableMixpanelTracking);
    }
  }, [settings]);

  const filteredMenus = useMemo(() => {
    const cloneMenus = JSON.parse(JSON.stringify(menus));
    const { restaurant_contracts } = restaurantInfo;
    if (!restaurant_contracts || !restaurant_contracts.length) return cloneMenus;

    const modes = restaurant_contracts.map((_) => _.distribution_mode);
    const hasOnlineOrderingV2Version = restaurant_contracts.some(
      (item) => item['online_ordering_version'] === 'v2' && item.distribution_mode === DISTRIBUTION_MODES.WHITE_LABEL
    );
    const hasCostPlus =
      restaurant_contracts.findIndex(
        (item) => item.distribution_mode === DISTRIBUTION_MODES.POS && !!item.cost_plus_start_at
      ) > -1;
    return cloneMenus.reduce((prev, menu) => {
      const { permission_code } = menu;

      if (permission_code === LOYALTY) {
        // no loyalty contract
        if (!modes.includes(DISTRIBUTION_MODES.LOYALTY)) return prev;

        // no loyalty crm contract
        if (!modes.includes(DISTRIBUTION_MODES.LOYALTY_CRM)) {
          menu.children = menu.children.filter((subMenu) => {
            return ![LOYALTY_AUTOPOLIT, LOYALTY_MANUAL_CAMPAIGN].includes(subMenu.permission_code);
          });
        }
      }

      if (permission_code === SETTING) {
        if (!modes.includes(DISTRIBUTION_MODES.WAIT_LIST)) {
          menu.children = menu.children.filter((subMenu) => {
            return subMenu.permission_code !== SETTING_WAITLIST;
          });
        }

        if (!modes.includes(DISTRIBUTION_MODES.RESERVATION)) {
          menu.children = menu.children.filter((subMenu) => {
            return subMenu.permission_code !== SETTING_RESERVATION;
          });
        }

        if (!modes.includes(DISTRIBUTION_MODES.SMART_ORDERING)) {
          menu.children = menu.children.filter((subMenu) => {
            return subMenu.permission_code !== SETTING_QRCODE_ORDERING;
          });
        }

        if (!hasOnlineOrderingV2Version) {
          menu.children = menu.children.filter((subMenu) => {
            return subMenu.permission_code !== SETTING_ONLINE_ORDERING;
          });
        }
      }

      // permission code is REPORTS, and not cost plus, filter PAYOUT REPORTS
      if (permission_code === REPORTS && !hasCostPlus) {
        menu.children = menu.children.filter((subMenu) => {
          return subMenu.permission_code !== REPORTS_PAYOUT;
        });
      }

      if (permission_code === PROMOTIONS && !modes.includes(DISTRIBUTION_MODES.PROMOTION)) {
        return prev;
      }

      if (permission_code === MENU) {
        menu.children.push({
          name: 'Menu Setup',
          foreign_name: '菜单设置',
          permission_code: MENU_SETUP,
          path: PageMenuSetup,
        });
      }

      prev.push(menu);
      return prev;
    }, []);
  }, [menus, restaurantInfo.restaurant_contracts]);

  const title = useMemo(() => {
    return getPageTitle(router.asPath, locale, menus);
  }, [router.asPath, locale, menus]);

  const handleMenuCollapse = () => {
    dispatch({
      type: appActions.changeLayoutCollapsed,
      payload: !collapsed,
    });
  };

  const layout = (
    <Layout>
      {!isInApp && (
        <SiderMenu
          logo={collapsed ? SmallLogo : logo}
          menuData={filteredMenus}
          loadingMenu={loadingMenu}
          isMobile={!screens.sm}
          onCollapse={handleMenuCollapse}
          collapsed={collapsed}
          theme="light"
          fixedSideBar={fixedSideBar}
          width={sideBarWidth}
        />
      )}
      <Layout
        style={{ minHeight: 'calc(100vh)', paddingTop: !isInApp ? 64 : 0, backgroundColor: layoutBackgroundColor }}
      >
        {!isInApp && (
          <GlobalHeader
            isMobile={!screens.sm}
            fixedSideBar={fixedSideBar}
            sideBarWidth={sideBarWidth}
            collapsed={collapsed}
          />
        )}
        <Content className={styles.content}>
          <ErrorBoundary>{props.children}</ErrorBoundary>
        </Content>
      </Layout>
      <Fonts />
    </Layout>
  );

  return (
    <Fragment>
      <Head>
        <title>{title}</title>
      </Head>
      {layout}
    </Fragment>
  );
};

export default BasicLayout;
